/**
 * Tujuan: Menyimpan data semua project portfolio (business, data analysis, thesis)
 * Caller: /pages/Projects.js, /pages/ProjectDetail.js, /pages/Home.js
 * Dependensi: Assets (gambar project/chart)
 * Main Functions: Object 'projects' yang berisi detail setiap project
 * Side Effects: Tidak ada (data static)
 * Changelog:
 *   2026-09 — Added thesis-absa entry (type: "thesis") with dummy placeholder data
 */
import dashboard from "../assets/dashboard.png";
import genreSales from "../assets/genre-sales.png";
import platform from "../assets/platform.png";

import energyDashboard from "../assets/energy.png";
import energyChart1 from "../assets/energy-chart1.png";
import energyChart2 from "../assets/energy-chart2.png";

import dataWarehouseChart1 from "../assets/cust_segment_chart.png";
import dataWarehouseChart2 from "../assets/revenue_trend_chart.png";

import tiketyukImg from "../assets/tiketyuk-logo.jpg";
import thesisImg from "../assets/thesis.png";

const projects = {

  // ================= TIKETYUK BUSINESS =================
  "tiketyuk": {
    type: "business",
    title: "TiketYuk — Concert Ticket War & Concierge Service",
    hook:
      "A real operating business serving 100+ concert & event tickets through trusted ticket war and concierge services.",

    tags: [
      "Next.js",
      "Vercel",
      "Google Sheets API",
      "Discord Webhook Automation",
      "JavaScript",
      "HTML/CSS"
    ],

    overview:
      "TiketYuk is a real business I created and led with a 4-person team, providing ticket war and ticket concierge services for concerts and high-demand events across Indonesia. Operating through X, Instagram, and TikTok, the business has successfully handled 100+ ticket orders. The business combines operational management, workflow automation, and digital ordering systems to improve customer experience and team efficiency.",

    businessHighlights: [
      { icon: "🎫", label: "100+ Tickets", desc: "Total tickets successfully sold" },
      { icon: "👥", label: "4-Person Team", desc: "Core operational team" },
      { icon: "📱", label: "3 Social Platforms", desc: "X, Instagram, TikTok" },
      { icon: "🌐", label: "Ordering Website", desc: "Online ordering system" }
    ],

    services: [
      {
        name: "Ticket Concierge (Jastip)",
        desc: "Full-service ticket handling: ticket purchase → physical ticket/wristband exchange → hand-delivery to the customer on concert day. Everything is managed end-to-end by our team.",
        icon: "🎟️"
      },
      {
        name: "Ticket War (Jaswar)",
        desc: "A service that helps customers secure high-demand tickets that are extremely difficult to obtain due to long online queues during ticket sales.",
        icon: "⚡"
      }
    ],

    team: [
      {
        role: "Social Media & Customer Support",
        count: 2,
        tasks: [
          "Social media management (X, Instagram, TikTok)",
          "Design & content writing for event promotions",
          "Customer communication & order handling",
          "Payment confirmation & customer follow-up"
        ]
      },
      {
        role: "System Development & Data Operations",
        count: 2,
        tasks: [
          "Website & automation workflow development",
          "Managing Discord notification automation",
          "Order monitoring & data validation",
          "Event research & operational coordination"
        ]
      }
    ],

    myRole: {
      position: "Team Lead",
      jobdesk: [
        "Initiated the business concept and designed the operational workflow",
        "Conducted event and ticketing research, including monitoring concert information, ticketing platforms, and high-demand event opportunities",
        "Co-developed automation workflows integrating website orders, Discord notifications, and spreadsheet database systems",
        "Led operational planning and coordinated team workflow across divisions",
        "Managed team coordination during high-traffic ticket sales and ticket war operations"
      ],
      competencies: [
        { icon: "📐", label: "Business Strategy" },
        { icon: "⚙️", label: "Workflow Optimization" },
        { icon: "💻", label: "Technical Development" },
        { icon: "🤝", label: "Operational Coordination" },
        { icon: "📊", label: "Data Management" }
      ]
    },

    orderingSystem: {
      title: "Website & Ordering System",
      desc: "The team developed a website to allow customers to place ticket orders in a more structured and efficient way. The website also includes an admin dashboard for the team to manage announcements, add new concerts/events, and monitor incoming orders.",
      features: [
        "Structured online ticket order form for customers",
        "Automated Discord notification when a new order is placed",
        "Order data automatically stored in Google Spreadsheet as a database",
        "Spreadsheet-based database chosen for easy access by non-technical team members",
        "Admin dashboard to edit announcements, manage concerts, and track orders",
        "Deployed on Vercel for public access"
      ],
      techFlow: [
        { step: "Customer", desc: "Fills in order form via website" },
        { step: "Website", desc: "Processes & validates order data" },
        { step: "Discord", desc: "Sends instant notification to team channel" },
        { step: "Google Sheets", desc: "Order data stored automatically as database" }
      ]
    },

    insights: [
      "High-demand ticket operations require structured coordination",
      "Automation helps reduce manual operational bottlenecks",
      "Centralized order tracking improves team collaboration",
      "Simplified systems improve usability for non-technical teams"
    ],

    impact: [
      "Improved operational efficiency during ticket sales",
      "Faster team response through Discord-based notifications",
      "More organized customer order management workflow",
      "Enhanced customer experience through structured processes"
    ],

    takeaway:
      "Building a real business from scratch teaches how to combine technical skills (web development, API integration) with practical, user-friendly operational needs.",

    website: "https://www.tiketyuk.me/",

    socials: {
      x: "https://x.com/tiketyuk",
      instagram: "https://www.instagram.com/tiketyukk",
      tiktok: "https://www.tiktok.com/@tiketyuk"
    },

    images: {
      cover: tiketyukImg
    }
  },

  "video-game-sales": {
    title: "Video Game Sales Analysis",
    hook:
      "Understanding how genre, platform, and region shape the global gaming market.",

    tags: [
      "Python",
      "Pandas",
      "NumPy",
      "Matplotlib",
      "Seaborn",
      "SciPy",
      "EDA",
      "Tableau",
      "Statistics"
    ],

    overview:
      "This project analyzes global video game sales data to uncover patterns across genre, platform, publisher, and region. The goal is to generate actionable insights for better decision making.",

    executive:
      "The video game industry is highly concentrated, with a few publishers dominating global sales. Success is driven by genre, platform, and regional trends; specifically, Action games lead global demand, while platform preferences vary by location. To maximize commercial impact, developers must abandon 'one-size-fits-all' approaches in favor of data-driven, market-specific strategies.",

    insights: [
      "Action is the best-performing genre globally",
      "Nintendo dominates the publisher landscape",
      "Regional preferences differ significantly",
      "Platform choice has a strong impact on sales performance"
    ],

    impact: [
      "Identify high-performing game genres",
      "Select optimal platforms for each region",
      "Improve market targeting strategies",
      "Support data-driven product decisions"
    ],

    takeaway:
      "Success depends on aligning genre, platform, and region strategy rather than relying on a single global approach.",

    visualInsights: {
      chart1: [
        "Action genre leads global sales",
        "Followed by Sports and Shooter",
        "Indicates high demand for fast-paced games"
      ],
      chart2: [
        "NA led by X360 (close to PS2)",
        "EU balanced, slight PlayStation edge",
        "Japan favors Nintendo, weak Xbox presence"
      ]
    },

    images: {
      genre: genreSales,
      platform: platform,
      dashboard: dashboard
    },

    github: "https://github.com/nabilahintaka/Video-Game-Sales-Analysis",
    demo: "https://public.tableau.com/views/VideoGameSalesDashboard_17376436610220/Dashboard"
  },

  // ================= ENERGY PROJECT =================
  "electricity-analysis": {
    title: "Electricity Energy Analysis Dashboard",
  
    hook:
      "Transforming raw electricity data into actionable insights for efficient energy management.",
  
    tags: [
      "Python",
      "Pandas",
      "NumPy",
      "Matplotlib",
      "Seaborn",
      "SciPy",
      "ETL",
      "PostgreSQL",
      "Tableau"
    ],
  
    overview:
      "This project builds an end-to-end data pipeline to analyze electricity consumption patterns. It focuses on cleaning, transforming, and visualizing energy data to uncover trends across time and regions. The goal is to support better decision-making in energy distribution and demand planning.",
  
    executive:
      "Electricity consumption follows consistent and predictable patterns driven by time-of-day and sector demand. Peak usage occurs during working hours, especially from industrial and commercial activities. By leveraging these insights, energy providers can optimize load distribution, improve forecasting accuracy, and reduce operational inefficiencies through data-driven strategies.",
  
    insights: [
      "Fossil fuel electricity strongly drives CO₂ emissions",
      "Renewable energy has not yet significantly reduced emissions",
      "Higher GDP per capita correlates with better electricity access",
      "Energy transition remains uneven across countries"
    ],
  
    impact: [
      "Support strategic planning for clean energy transition",
      "Identify high-emission regions for targeted intervention",
      "Understand relationship between economic growth and energy access",
      "Enable data-driven policy for sustainable energy investment"
    ],
  
    takeaway:
      "Understanding electricity usage patterns enables more efficient energy management, helping providers optimize supply, reduce costs, and improve reliability through data-driven decisions.",
  
    visualInsights: {
      chart1: [
        "Strong positive correlation between fossil fuels and CO₂ emissions",
        "Higher fossil dependency leads to higher emissions",
        "Confirms fossil fuels as primary emission driver"
      ],
      chart2: [
        "Renewable energy adoption varies significantly across countries",
        "Higher-income countries achieve better electricity access",
        "Developing countries face energy inequality challenges"
      ]
    },
  
    images: {
      chart1: energyChart1,
      chart2: energyChart2,
      dashboard: energyDashboard
    },
  
    github: "https://github.com/nabilahintaka/Electricity-Energy-Analyst",
    demo: "https://public.tableau.com/views/ElectricalEnergyDashboard/Dashboard1",
  },

  "thelook-dwh": {
    title: "E-Commerce Data Warehouse Analysis",

    hook:
      "Transforming raw transactional data into structured business insights using a scalable data warehouse.",

    tags: [
      "Python",
      "SQL",
      "PostgreSQL",
      "Data Warehouse",
      "Data Modeling"
    ],

    overview:
      "This project builds a structured data warehouse from raw e-commerce transactional data using a star schema model. The goal is to enable scalable and efficient analysis across products, customers, and time dimensions.",

    executive:
      "Revenue is highly concentrated in a small number of products and high-value customers. Customer purchasing behavior is uneven, indicating strong segmentation opportunities. The data warehouse structure enables faster querying and supports scalable, data-driven decision-making.",

    insights: [
      "Revenue is dominated by a small number of products",
      "Customer spending is highly skewed (Pareto-like distribution)",
      "A few product categories contribute most of the total sales",
      "Revenue growth begins to stabilize over time"
    ],

    impact: [
      "Identify high-performing products",
      "Target high-value customers effectively",
      "Optimize product category strategy",
      "Enable scalable business analytics"
    ],

    takeaway:
      "Well-structured data models are essential to unlock scalable analytics and meaningful business insights.",

    visualInsights: {
      chart1: [
        "Revenue shows consistent upward trend",
        "Growth begins to slow in later periods",
        "Indicates potential market saturation phase"
      ],
      chart2: [
        "Majority of customers are low spenders",
        "Small segment contributes most revenue",
        "Strong opportunity for customer segmentation strategy"
      ]
    },

    images: {
      chart1: dataWarehouseChart1,
      chart2: dataWarehouseChart2,
    },

    github: "https://github.com/nabilahintaka/Data-Warehouse-TheLook"
  },

  // ================= THESIS =================
  "thesis-absa": {
    type: "thesis",
    title: "Klasifikasi Multi-Aspect dalam Aspect-Based Sentiment Analysis (ABSA) pada Ulasan KlikIndomaret menggunakan Model Transformer",
    hook: "Memahami persepsi pelanggan KlikIndomaret secara mendalam melalui klasifikasi sentimen berbasis aspek pada ~3.000 ulasan Google Play Store (Juli–Agustus 2026) menggunakan IndoBERT dan IndoRoBERTa.",
    status: "In Progress",

    tags: ["NLP", "ABSA", "IndoBERT", "IndoRoBERTa", "Transformer", "Python", "PyTorch", "Hugging Face", "CRISP-DM"],

    overview:
      "Penelitian ini menerapkan pendekatan Aspect-Based Sentiment Analysis (ABSA) dengan metode multi-aspect classification untuk menganalisis sentimen pada ulasan pengguna aplikasi KlikIndomaret di Google Play Store. Menggunakan model Transformer berbahasa Indonesia — IndoBERT (indobenchmark/indobert-base-p1) dan IndoRoBERTa (flax-community/indonesian-roberta-base) — penelitian ini mengklasifikasikan sentimen ke dalam lima aspek: Aplikasi, Produk, Harga, Layanan, dan Pembayaran. Dataset terdiri dari ~3.000 ulasan periode Juli–Agustus 2026 dengan pendekatan multi-aspect sehingga satu ulasan dapat memiliki lebih dari satu aspek.",

    background:
      "KlikIndomaret telah diunduh lebih dari 10 juta kali dengan 206 ribu+ ulasan di Google Play Store. Data internal PT Indomarco Prismatama menunjukkan penurunan aktivitas transaksi pada periode Juli–Agustus 2026. Analisis sentimen tradisional hanya memberikan satu label untuk keseluruhan ulasan, sehingga tidak mampu menangkap permasalahan per aspek secara spesifik. ABSA dengan model Transformer dipilih karena mampu memahami konteks bahasa Indonesia yang informal, slang, dan multi-aspek secara lebih akurat dibanding pendekatan berbasis leksikon atau machine learning klasik.",

    researchQuestions: [
      "Bagaimana proses implementasi ABSA dengan pendekatan multi-aspect dan klasifikasi sentimen berbasis aspek menggunakan model Transformer pada ulasan pengguna KlikIndomaret?",
      "Bagaimana perbandingan performa model IndoBERT dan IndoRoBERTa dalam melakukan aspect-level sentiment classification pada ulasan pengguna KlikIndomaret berdasarkan metrik Accuracy, Precision, Recall, dan F1-score?",
      "Bagaimana distribusi sentimen (positif, netral, dan negatif) pada setiap aspek dalam ulasan pengguna KlikIndomaret?"
    ],

    aspects: [
      {
        name: "Aplikasi", icon: "📱",
        desc: "Performa, UI/UX, stabilitas sistem, fitur, bug, dan pengalaman penggunaan aplikasi",
        example: "\"Aplikasinya sering error dan lemot banget, login aja susah\"",
        keywords: ["aplikasi", "login", "error", "lemot", "fitur", "bug", "update"]
      },
      {
        name: "Produk", icon: "🛍️",
        desc: "Kualitas, ketersediaan stok, kesesuaian, kesegaran, dan variasi produk",
        example: "\"Stok sering kosong, barang yang diterima sudah expired\"",
        keywords: ["barang", "produk", "stok", "segar", "rusak", "expired"]
      },
      {
        name: "Harga", icon: "💰",
        desc: "Harga produk, promo, diskon, dan kesesuaian nilai dengan harga yang dibayar",
        example: "\"Harganya lebih murah dari minimarket, promonya banyak\"",
        keywords: ["harga", "promo", "diskon", "murah", "mahal"]
      },
      {
        name: "Layanan", icon: "🎧",
        desc: "Customer service, respons keluhan, pengiriman, kurir, dan proses refund",
        example: "\"CS tidak merespons keluhan, pengiriman sangat lambat\"",
        keywords: ["customer service", "CS", "respon", "pengiriman", "kurir", "refund"]
      },
      {
        name: "Pembayaran", icon: "💳",
        desc: "Metode pembayaran, proses checkout, QRIS, virtual account, dan keberhasilan transaksi",
        example: "\"Gagal bayar terus padahal saldo cukup, QRIS-nya error\"",
        keywords: ["bayar", "transfer", "saldo", "QRIS", "virtual account", "gagal bayar", "metode pembayaran"]
      }
    ],

    pipeline: [
      { step: "Pengumpulan Data", icon: "🔍", desc: "Scraping ~3.000 ulasan KlikIndomaret dari Google Play Store periode Juli–Agustus 2026 menggunakan library google-play-scraper." },
      { step: "Pra-pemrosesan", icon: "🧹", desc: "Case folding, cleaning text (hapus URL, emoji, simbol), normalisasi kata slang (\"gk\"→\"tidak\", \"apk\"→\"aplikasi\"), tokenisasi sesuai tokenizer masing-masing model." },
      { step: "Identifikasi Aspek", icon: "🏷️", desc: "Pelabelan aspek dengan pendekatan keyword-based untuk 5 kategori. Validasi manual 500 sampel ulasan (prioritas multi-aspek) untuk memastikan akurasi label." },
      { step: "Dataset Multi-Aspect", icon: "📂", desc: "Teknik explode dataset: satu ulasan dengan banyak aspek dipecah menjadi beberapa pasangan review–aspect. Anotasi sentimen (positif/netral/negatif) secara manual." },
      { step: "Fine-tuning", icon: "⚙️", desc: "Fine-tuning IndoBERT & IndoRoBERTa menggunakan Hugging Face Transformers. Learning rate 2×10⁻⁵, AdamW optimizer, 5 epoch, batch size 16, max sequence length 256." },
      { step: "Evaluasi", icon: "📊", desc: "Evaluasi dan perbandingan kedua model menggunakan Accuracy, Precision, Recall, F1-Score, dan Confusion Matrix per aspek sentimen." }
    ],

    modelComparison: [
      {
        model: "IndoBERT",
        modelId: "indobenchmark/indobert-base-p1",
        desc: "Model BERT bidirectional dilatih pada korpus Bahasa Indonesia (IndoNLU corpus Indo4B). Memahami konteks kata dari kiri dan kanan secara bersamaan.",
        pros: [
          "Dilatih khusus pada korpus Bahasa Indonesia",
          "Arsitektur bidirectional encoder — konteks dua arah",
          "Akurasi 98,53% & F1 97,24% pada klasifikasi teks Bahasa Indonesia (Liem et al., 2025)",
          "Relevan untuk teks ulasan informal & tidak terstruktur"
        ],
        cons: [
          "Butuh sumber daya komputasi lebih besar dari model klasik",
          "Performa bergantung pada kualitas data berlabel & keseimbangan kelas"
        ]
      },
      {
        model: "IndoRoBERTa",
        modelId: "flax-community/indonesian-roberta-base",
        desc: "Model RoBERTa (124M parameter) dilatih from scratch pada korpus Bahasa Indonesia dari dataset OSCAR. Mengoptimalkan BERT melalui dynamic masking, penghapusan NSP, dan data lebih besar.",
        pros: [
          "Dynamic masking → representasi kontekstual lebih kaya",
          "Penghapusan Next Sentence Prediction (NSP) → pelatihan lebih efisien",
          "Akurasi 98% & F1 97,4% pada klasifikasi sentimen Bahasa Indonesia (Kurniadi et al., 2024)",
          "Lebih robust untuk teks informal media sosial"
        ],
        cons: [
          "Lebih berat secara komputasi",
          "Fine-tuning memerlukan resource lebih besar"
        ]
      }
    ],

    finetuningParams: [
      { param: "Learning Rate", value: "2 × 10⁻⁵" },
      { param: "Optimizer", value: "AdamW" },
      { param: "Batch Size", value: "16" },
      { param: "Jumlah Epoch", value: "5" },
      { param: "Max Sequence Length", value: "256" },
      { param: "LR Scheduler", value: "Linear Scheduler" },
      { param: "Loss Function", value: "Cross Entropy Loss" }
    ],

    // Dummy placeholder results — to be replaced with actual research results
    results: {
      note: "[PLACEHOLDER] Data hasil eksperimen akan diisi setelah proses fine-tuning selesai.",
      indobert: {
        overall_f1: "[TBD]",
        perAspect: [
          { aspect: "Aplikasi",   f1: "[TBD]", accuracy: "[TBD]" },
          { aspect: "Produk",     f1: "[TBD]", accuracy: "[TBD]" },
          { aspect: "Harga",      f1: "[TBD]", accuracy: "[TBD]" },
          { aspect: "Layanan",    f1: "[TBD]", accuracy: "[TBD]" },
          { aspect: "Pembayaran", f1: "[TBD]", accuracy: "[TBD]" }
        ]
      },
      indoroberta: {
        overall_f1: "[TBD]",
        perAspect: [
          { aspect: "Aplikasi",    f1: "[TBD]", accuracy: "[TBD]" },
          { aspect: "Produk",      f1: "[TBD]", accuracy: "[TBD]" },
          { aspect: "Harga",       f1: "[TBD]", accuracy: "[TBD]" },
          { aspect: "Layanan",     f1: "[TBD]", accuracy: "[TBD]" },
          { aspect: "Pembayaran",  f1: "[TBD]", accuracy: "[TBD]" }
        ]
      }
    },

    tools: [
      "Python", "PyTorch", "Hugging Face Transformers",
      "indobenchmark/indobert-base-p1", "flax-community/indonesian-roberta-base",
      "google-play-scraper", "Pandas", "NumPy", "Scikit-learn",
      "Google Colab", "CRISP-DM"
    ],

    takeaway:
      "Penelitian ini mengimplementasikan pipeline ABSA end-to-end — dari scraping ulasan, preprocessing, identifikasi aspek keyword-based, validasi manual, pembentukan dataset multi-aspect, hingga fine-tuning dan evaluasi dua model Transformer. Hasil perbandingan IndoBERT dan IndoRoBERTa diharapkan memberikan kontribusi pada NLP Bahasa Indonesia dan insight strategis bagi Indomaret dalam meningkatkan kualitas layanan berdasarkan persepsi pengguna per aspek.",

    images: {
      cover: thesisImg
    }
  }
};

export default projects;
